import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import CommercialHeader from "@/components/commercial/CommercialHeader";
import { api, fmtEur, fmtDate, fmtDateTime } from "@/lib/commercialApi";
import { COMMERCIAL_STAGES, stageById, APPOINTMENT_STATUSES } from "@/lib/commercialStages";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Loader2, Phone, Mail, Building2, Upload, Trash2, Play, PlusCircle, AlertCircle, MoreHorizontal } from "lucide-react";
import { missingFields } from "@/lib/commercialUx";

const PRIMARY_TABS = [
  { id: "resume", label: "Résumé" },
  { id: "appels", label: "Appels & Audio" },
  { id: "rdv", label: "Rendez-vous" },
  { id: "opp", label: "Opportunité" },
];
const MORE_TABS = [
  { id: "entreprise", label: "Entreprise" },
  { id: "contacts", label: "Contacts" },
  { id: "audio", label: "Audio seul" },
  { id: "transcriptions", label: "Transcriptions IA" },
  { id: "propositions", label: "Propositions" },
  { id: "contrat", label: "Contrat" },
  { id: "commissions", label: "Commissions" },
];
const ALL_TABS = [...PRIMARY_TABS, ...MORE_TABS];

export default function ComFicheCommerciale() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState({ stages: COMMERCIAL_STAGES, teleprospectrices: [], commerciaux: [] });
  const [activeTab, setActiveTab] = useState("resume");
  const [moreOpen, setMoreOpen] = useState(false);

  useEffect(() => {
    api.get("/meta").then((r) => setMeta(r.data));
    load();
    // eslint-disable-next-line
  }, [id]);

  async function load() {
    try {
      const r = await api.get(`/opportunities/${id}`);
      setData(r.data);
    } catch {
      toast.error("Opportunité introuvable");
    }
  }

  async function update(fields) {
    try {
      await api.patch(`/opportunities/${id}`, fields);
      await load();
      toast.success("Enregistré");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-brand-paper">
        <CommercialHeader />
        <div className="max-w-[1400px] mx-auto px-6 py-10 flex items-center gap-2 text-brand-subtle">
          <Loader2 className="h-4 w-4 animate-spin" /> Chargement…
        </div>
      </div>
    );
  }

  const { opportunity: o, prospect, calls, appointments, contracts, commissions, audio_files } = data;
  const stage = stageById(o.stage);
  const nextRdv = (appointments || []).find((a) => ["programme", "confirme"].includes(a.status));
  const missing = missingFields(o);
  const noTelepro = !o.teleprospectrice;
  const noCommercial = !o.commercial;

  return (
    <div className="min-h-screen bg-brand-paper">
      <CommercialHeader />
      <div className="max-w-[1400px] mx-auto px-4 lg:px-10 py-6 lg:py-8">
        <Link to="/commercial/opportunites" className="text-xs text-brand-subtle hover:text-brand-ink">← Retour au pipeline</Link>

        {/* Alertes contextuelles */}
        {(noTelepro || noCommercial) && (
          <div className="mt-3 rounded-lg border border-[#E9DED8] bg-[#F8F3F0] p-4 flex items-start gap-3" data-testid="alert-attribution">
            <AlertCircle className="h-4 w-4 text-[#7A4B3A] flex-shrink-0 mt-0.5" strokeWidth={1.75} />
            <div className="flex-1">
              <div className="text-sm font-medium text-[#7A4B3A]">Attribution manquante</div>
              <div className="text-xs text-[#7A4B3A]/80 mt-0.5">
                {noTelepro && noCommercial
                  ? "Aucune téléprospectrice ni commercial responsable n'est assigné."
                  : noTelepro ? "Pas de téléprospectrice assignée." : "Pas de commercial responsable."}
              </div>
              <div className="mt-2 flex gap-2 flex-wrap">
                {noTelepro && (
                  <select onChange={(e) => e.target.value && update({ teleprospectrice: e.target.value })} className="h-8 px-2 text-xs rounded-md border border-[#E9DED8] bg-white" data-testid="alert-assign-telepro">
                    <option value="">Choisir téléprospectrice…</option>
                    {meta.teleprospectrices.map((t) => <option key={t}>{t}</option>)}
                  </select>
                )}
                {noCommercial && (
                  <select onChange={(e) => e.target.value && update({ commercial: e.target.value })} className="h-8 px-2 text-xs rounded-md border border-[#E9DED8] bg-white" data-testid="alert-assign-commercial">
                    <option value="">Choisir commercial…</option>
                    {meta.commerciaux.map((c) => <option key={c}>{c}</option>)}
                  </select>
                )}
              </div>
            </div>
          </div>
        )}

        {missing.length >= 2 && (
          <div className="mt-3 rounded-lg border border-[#E9E2CF] bg-[#FAF8F1] p-4 flex items-start gap-3" data-testid="alert-incomplete">
            <AlertCircle className="h-4 w-4 text-[#6B5B33] flex-shrink-0 mt-0.5" strokeWidth={1.75} />
            <div className="flex-1">
              <div className="text-sm font-medium text-[#6B5B33]">Cette opportunité est incomplète</div>
              <div className="text-xs text-[#6B5B33]/80 mt-0.5">
                Champs manquants : {missing.join(", ")}
              </div>
            </div>
            <button
              onClick={() => setActiveTab("resume")}
              data-testid="alert-complete-btn"
              className="h-8 px-3 rounded-md bg-brand-ink text-white text-xs hover:bg-brand-moss flex-shrink-0"
            >
              Compléter la fiche
            </button>
          </div>
        )}

        {/* Header */}
        <div className="mt-3 rounded-lg border border-brand-line bg-white p-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <h1 className="font-display text-2xl text-brand-ink truncate">{o.entreprise_name}</h1>
              <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-brand-subtle">
                {o.contact_nom && <span className="inline-flex items-center gap-1"><Building2 className="h-3 w-3" />{o.contact_nom}</span>}
                {o.contact_telephone && <a href={`tel:${o.contact_telephone}`} className="inline-flex items-center gap-1 hover:text-brand-ink"><Phone className="h-3 w-3" />{o.contact_telephone}</a>}
                {o.contact_email && <a href={`mailto:${o.contact_email}`} className="inline-flex items-center gap-1 hover:text-brand-ink"><Mail className="h-3 w-3" />{o.contact_email}</a>}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-[11px] font-medium px-2.5 py-1 rounded-md border ${stage.color}`}>{stage.label}</span>
              <select
                value={o.stage}
                onChange={(e) => update({ stage: e.target.value })}
                data-testid="fiche-stage-select"
                className="h-8 px-2 text-xs rounded-md border border-brand-line bg-white"
              >
                {COMMERCIAL_STAGES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 lg:grid-cols-5 gap-3 text-xs">
            <HeaderStat label="Téléprospectrice" value={o.teleprospectrice || "—"} />
            <HeaderStat label="Commercial" value={o.commercial || "—"} />
            <HeaderStat label="Montant potentiel" value={fmtEur(o.montant_estime || o.montant_final)} />
            <HeaderStat label="Prochain RDV" value={nextRdv ? fmtDateTime(nextRdv.scheduled_at) : "—"} />
            <HeaderStat label="Prochaine action" value={o.prochaine_action ? `${o.prochaine_action}${o.date_prochaine_action ? ` · ${fmtDateTime(o.date_prochaine_action)}` : ""}` : "—"} />
          </div>
        </div>

        {/* Tabs : 4 principaux + menu Plus */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-6">
          <TabsList className="flex flex-wrap h-auto bg-white border border-brand-line p-1 gap-0.5">
            {PRIMARY_TABS.map((t) => (
              <TabsTrigger key={t.id} value={t.id} data-testid={`tab-${t.id}`} className="text-xs">{t.label}</TabsTrigger>
            ))}
            <div className="relative" onMouseLeave={() => setMoreOpen(false)}>
              <button
                type="button"
                onClick={() => setMoreOpen((o) => !o)}
                data-testid="tab-more"
                className={`inline-flex items-center gap-1 h-8 px-3 text-xs rounded-sm ${
                  MORE_TABS.some((t) => t.id === activeTab)
                    ? "bg-brand-ink text-white"
                    : "text-brand-ink/80 hover:bg-brand-paper"
                }`}
              >
                <MoreHorizontal className="h-3.5 w-3.5" /> Plus
              </button>
              {moreOpen && (
                <div className="absolute left-0 mt-0.5 w-56 rounded-md border border-brand-line bg-white shadow-card py-1 z-20" data-testid="tab-more-menu">
                  {MORE_TABS.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => { setActiveTab(t.id); setMoreOpen(false); }}
                      data-testid={`tab-more-${t.id}`}
                      className={`block w-full text-left px-3 py-1.5 text-xs ${
                        activeTab === t.id ? "bg-brand-paper text-brand-ink" : "text-brand-ink/80 hover:bg-brand-paper"
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </TabsList>

          <TabsContent value="resume" className="mt-4">
            <ResumeTab o={o} prospect={prospect} meta={meta} onUpdate={update} />
          </TabsContent>
          <TabsContent value="appels" className="mt-4">
            <AppelsTab oppId={o.id} siret={o.siret} teleprospectrice={o.teleprospectrice} calls={calls} meta={meta} onReload={load} />
            <div className="mt-4"><AudioTab calls={calls} audio_files={audio_files} onReload={load} /></div>
          </TabsContent>
          <TabsContent value="rdv" className="mt-4">
            <RdvTab oppId={o.id} opp={o} appointments={appointments} meta={meta} onReload={load} />
          </TabsContent>
          <TabsContent value="opp" className="mt-4">
            <OppTab o={o} onUpdate={update} />
          </TabsContent>
          <TabsContent value="entreprise" className="mt-4">
            <EntrepriseTab prospect={prospect} o={o} />
          </TabsContent>
          <TabsContent value="contacts" className="mt-4">
            <ContactsTab prospect={prospect} o={o} />
          </TabsContent>
          <TabsContent value="audio" className="mt-4">
            <AudioTab calls={calls} audio_files={audio_files} onReload={load} />
          </TabsContent>
          <TabsContent value="transcriptions" className="mt-4">
            <div className="rounded-lg border border-brand-line bg-white p-6">
              <div className="eyebrow mb-2">Transcriptions &amp; résumés IA</div>
              <p className="text-sm text-brand-subtle">
                Les transcriptions apparaissent sous chaque appel dans l'onglet « Appels &amp; Audio » dès qu'un MP3 est importé.
              </p>
            </div>
          </TabsContent>
          <TabsContent value="propositions" className="mt-4">
            <PropositionsTab o={o} onUpdate={update} />
          </TabsContent>
          <TabsContent value="contrat" className="mt-4">
            <ContratTab oppId={o.id} contracts={contracts} onReload={load} />
          </TabsContent>
          <TabsContent value="commissions" className="mt-4">
            <CommissionsTab commissions={commissions} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function HeaderStat({ label, value }) {
  return (
    <div className="rounded-md border border-brand-line bg-brand-paper p-2.5">
      <div className="text-[10px] uppercase tracking-wider text-brand-subtle">{label}</div>
      <div className="mt-1 text-sm text-brand-ink font-medium">{value}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="eyebrow mb-1 block">{label}</Label>
      {children}
    </div>
  );
}

function ResumeTab({ o, prospect, meta, onUpdate }) {
  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <div className="rounded-lg border border-brand-line bg-white p-5 space-y-3">
        <h3 className="font-display text-lg text-brand-ink">Qualification</h3>
        <Field label="Besoin identifié"><Textarea defaultValue={o.besoin} onBlur={(e) => onUpdate({ besoin: e.target.value })} data-testid="field-besoin" rows={3} /></Field>
        <Field label="Budget évoqué"><Input defaultValue={o.budget_evoque} onBlur={(e) => onUpdate({ budget_evoque: e.target.value })} /></Field>
        <Field label="Objections"><Textarea defaultValue={o.objections} onBlur={(e) => onUpdate({ objections: e.target.value })} rows={2} /></Field>
      </div>
      <div className="rounded-lg border border-brand-line bg-white p-5 space-y-3">
        <h3 className="font-display text-lg text-brand-ink">Attribution &amp; prochaine étape</h3>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Téléprospectrice">
            <select defaultValue={o.teleprospectrice || ""} onBlur={(e) => onUpdate({ teleprospectrice: e.target.value })} className="h-10 w-full px-3 text-sm rounded-md border border-brand-line bg-white" data-testid="field-telepro">
              <option value="">—</option>
              {meta.teleprospectrices.map((t) => <option key={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Commercial responsable">
            <select defaultValue={o.commercial || ""} onBlur={(e) => onUpdate({ commercial: e.target.value })} className="h-10 w-full px-3 text-sm rounded-md border border-brand-line bg-white" data-testid="field-commercial">
              <option value="">—</option>
              {meta.commerciaux.map((c) => <option key={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Montant estimé (€)"><Input type="number" defaultValue={o.montant_estime || ""} onBlur={(e) => onUpdate({ montant_estime: parseFloat(e.target.value) || null })} /></Field>
          <Field label="Probabilité (%)"><Input type="number" min="0" max="100" defaultValue={o.probabilite || 0} onBlur={(e) => onUpdate({ probabilite: parseInt(e.target.value, 10) || 0 })} /></Field>
          <Field label="Prochaine action"><Input defaultValue={o.prochaine_action} onBlur={(e) => onUpdate({ prochaine_action: e.target.value })} /></Field>
          <Field label="Date prochaine action"><Input type="datetime-local" defaultValue={o.date_prochaine_action ? o.date_prochaine_action.slice(0, 16) : ""} onBlur={(e) => onUpdate({ date_prochaine_action: e.target.value || null })} /></Field>
        </div>
        <Field label="Notes internes"><Textarea defaultValue={o.notes_internes} onBlur={(e) => onUpdate({ notes_internes: e.target.value })} rows={3} /></Field>
      </div>
    </div>
  );
}

function EntrepriseTab({ prospect, o }) {
  if (!prospect) return <div className="rounded-lg border border-brand-line bg-white p-6 text-sm text-brand-subtle">Prospect introuvable (données supprimées).</div>;
  return (
    <div className="rounded-lg border border-brand-line bg-white p-5 grid sm:grid-cols-2 gap-3 text-sm">
      <Row label="Nom" value={prospect.nom} />
      <Row label="SIRET" value={o.siret} />
      <Row label="SIREN" value={o.siren} />
      <Row label="Code NAF" value={prospect.code_naf} />
      <Row label="Libellé NAF" value={prospect.libelle_naf} />
      <Row label="Adresse" value={prospect.adresse} />
      <Row label="Ville" value={`${prospect.code_postal || ""} ${prospect.ville || ""}`} />
      <Row label="Effectif" value={prospect.effectif_label} />
      <Row label="Chiffre d'affaires" value={fmtEur(prospect.ca)} />
      <Row label="Résultat net" value={fmtEur(prospect.resultat_net)} />
      <Row label="Site web" value={prospect.google_website || "—"} />
      <Row label="Google Maps" value={prospect.google_rating ? `${prospect.google_rating} ★ (${prospect.google_reviews_count || 0} avis)` : "—"} />
    </div>
  );
}

function ContactsTab({ prospect, o }) {
  const contacts = (prospect?.dirigeants_contacts || []);
  if (!contacts.length) {
    return (
      <div className="rounded-lg border border-brand-line bg-white p-5 text-sm">
        <div className="font-medium text-brand-ink mb-2">Contact principal</div>
        <div className="text-brand-subtle">{o.contact_nom || "—"} · {o.contact_telephone || "—"} · {o.contact_email || "—"}</div>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {contacts.map((c, i) => (
        <div key={i} className="rounded-lg border border-brand-line bg-white p-4 grid sm:grid-cols-4 gap-2 text-sm">
          <div className="font-medium">{c.prenoms || ""} {c.nom || ""}</div>
          <div className="text-brand-subtle">{c.qualite || c.poste_site || "—"}</div>
          <div className="text-brand-subtle">{c.phone || "—"}</div>
          <div className="text-brand-subtle">{c.email || "—"}</div>
        </div>
      ))}
    </div>
  );
}

function AppelsTab({ oppId, siret, teleprospectrice, calls, meta, onReload }) {
  const [form, setForm] = useState({ note: "", outcome_status: "appele", teleprospectrice: teleprospectrice || "" });
  const [saving, setSaving] = useState(false);
  async function add(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/calls", { opportunity_id: oppId, siret, ...form });
      setForm({ note: "", outcome_status: "appele", teleprospectrice: teleprospectrice || "" });
      onReload();
      toast.success("Appel enregistré");
    } catch { toast.error("Erreur"); } finally { setSaving(false); }
  }
  return (
    <div className="space-y-4">
      <form onSubmit={add} className="rounded-lg border border-brand-line bg-white p-4 grid lg:grid-cols-5 gap-3 items-end">
        <Field label="Téléprospectrice">
          <select value={form.teleprospectrice} onChange={(e) => setForm({ ...form, teleprospectrice: e.target.value })} className="h-9 w-full px-2 text-sm rounded-md border border-brand-line bg-white">
            <option value="">—</option>
            {meta.teleprospectrices.map((t) => <option key={t}>{t}</option>)}
          </select>
        </Field>
        <Field label="Résultat">
          <select value={form.outcome_status} onChange={(e) => setForm({ ...form, outcome_status: e.target.value })} className="h-9 w-full px-2 text-sm rounded-md border border-brand-line bg-white">
            <option value="appele">Note simple</option>
            <option value="pas_repondu">Pas répondu</option>
            <option value="message_vocal">Message vocal</option>
            <option value="rappel">Rappel</option>
            <option value="interesse">Intéressé</option>
            <option value="rdv_pris">RDV pris</option>
            <option value="pas_interesse">Pas intéressé</option>
          </select>
        </Field>
        <div className="lg:col-span-2">
          <Field label="Note"><Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="call-note" /></Field>
        </div>
        <Button type="submit" disabled={saving} data-testid="add-call">{saving ? "..." : "Ajouter"}</Button>
      </form>
      {(calls || []).length === 0 ? (
        <div className="text-sm text-brand-subtle">Aucun appel enregistré.</div>
      ) : (
        <div className="rounded-lg border border-brand-line bg-white divide-y divide-brand-line">
          {calls.map((c) => (
            <div key={c.id} className="p-3 text-sm grid grid-cols-1 sm:grid-cols-6 gap-2 items-start">
              <div className="text-brand-subtle font-mono text-[11px]">{fmtDateTime(c.started_at)}</div>
              <div className="text-brand-ink">{c.teleprospectrice || "—"}</div>
              <div className="text-brand-ink">{c.outcome_status}</div>
              <div className="sm:col-span-3 text-brand-ink/80">{c.note || c.besoin || "—"}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RdvTab({ oppId, opp, appointments, meta, onReload }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    scheduled_at: "", duration_min: 60,
    commercial: opp.commercial || "", teleprospectrice: opp.teleprospectrice || "",
    interlocuteur_nom: "", interlocuteur_role: "", interlocuteur_telephone: "", interlocuteur_email: "",
    besoin: "", urgence: "", budget_estime: "", budget_inconnu: false,
    disponibilite: "", interet: 3, prochaine_etape: "",
  });

  async function create(e) {
    e.preventDefault();
    try {
      await api.post("/appointments", { opportunity_id: oppId, ...form });
      setOpen(false);
      onReload();
      toast.success("RDV créé");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  }

  async function updateStatus(id, status) {
    try { await api.patch(`/appointments/${id}`, { status }); onReload(); toast.success("RDV mis à jour"); }
    catch { toast.error("Erreur"); }
  }

  return (
    <div className="space-y-3">
      {!open ? (
        <Button onClick={() => setOpen(true)} data-testid="rdv-new"><PlusCircle className="h-4 w-4 mr-2" />Nouveau rendez-vous</Button>
      ) : (
        <form onSubmit={create} className="rounded-lg border border-brand-line bg-white p-5 space-y-3">
          <div className="eyebrow">Qualification obligatoire</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <Field label="Date & heure *"><Input type="datetime-local" required value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} /></Field>
            <Field label="Durée (min)"><Input type="number" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: parseInt(e.target.value, 10) || 60 })} /></Field>
            <Field label="Commercial">
              <select value={form.commercial} onChange={(e) => setForm({ ...form, commercial: e.target.value })} className="h-10 w-full px-3 text-sm rounded-md border border-brand-line bg-white">
                <option value="">—</option>
                {meta.commerciaux.map((c) => <option key={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Interlocuteur *"><Input required value={form.interlocuteur_nom} onChange={(e) => setForm({ ...form, interlocuteur_nom: e.target.value })} /></Field>
            <Field label="Rôle"><Input value={form.interlocuteur_role} onChange={(e) => setForm({ ...form, interlocuteur_role: e.target.value })} /></Field>
            <Field label="Téléphone"><Input value={form.interlocuteur_telephone} onChange={(e) => setForm({ ...form, interlocuteur_telephone: e.target.value })} /></Field>
            <Field label="Email"><Input value={form.interlocuteur_email} onChange={(e) => setForm({ ...form, interlocuteur_email: e.target.value })} /></Field>
            <Field label="Urgence">
              <select value={form.urgence} onChange={(e) => setForm({ ...form, urgence: e.target.value })} className="h-10 w-full px-3 text-sm rounded-md border border-brand-line bg-white">
                <option value="">—</option><option>Immédiat</option><option>&lt; 1 mois</option><option>&lt; 3 mois</option><option>&gt; 3 mois</option>
              </select>
            </Field>
            <Field label="Niveau d'intérêt (1-5)"><Input type="number" min="1" max="5" value={form.interet} onChange={(e) => setForm({ ...form, interet: parseInt(e.target.value, 10) || 3 })} /></Field>
            <Field label="Budget estimé"><Input value={form.budget_estime} onChange={(e) => setForm({ ...form, budget_estime: e.target.value })} disabled={form.budget_inconnu} /></Field>
            <label className="flex items-center gap-2 text-xs text-brand-subtle mt-6"><input type="checkbox" checked={form.budget_inconnu} onChange={(e) => setForm({ ...form, budget_inconnu: e.target.checked })} /> Budget inconnu</label>
            <Field label="Disponibilité"><Input value={form.disponibilite} onChange={(e) => setForm({ ...form, disponibilite: e.target.value })} /></Field>
          </div>
          <Field label="Besoin identifié *"><Textarea required rows={2} value={form.besoin} onChange={(e) => setForm({ ...form, besoin: e.target.value })} /></Field>
          <Field label="Prochaine étape *"><Input required value={form.prochaine_etape} onChange={(e) => setForm({ ...form, prochaine_etape: e.target.value })} /></Field>
          <div className="flex gap-2">
            <Button type="submit" data-testid="rdv-submit">Créer le RDV</Button>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
          </div>
        </form>
      )}
      {(appointments || []).length === 0 ? (
        <div className="text-sm text-brand-subtle mt-4">Aucun RDV planifié.</div>
      ) : (
        <div className="space-y-2">
          {appointments.map((a) => (
            <div key={a.id} className="rounded-lg border border-brand-line bg-white p-3 flex items-center justify-between gap-3 text-sm flex-wrap">
              <div>
                <div className="font-medium">{fmtDateTime(a.scheduled_at)} · {a.interlocuteur_nom}</div>
                <div className="text-brand-subtle text-xs mt-0.5">{a.besoin}</div>
              </div>
              <div className="flex items-center gap-2">
                <select value={a.status} onChange={(e) => updateStatus(a.id, e.target.value)} className="h-8 px-2 text-xs rounded-md border border-brand-line bg-white">
                  {APPOINTMENT_STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function OppTab({ o, onUpdate }) {
  return (
    <div className="rounded-lg border border-brand-line bg-white p-5 grid sm:grid-cols-2 gap-3 text-sm">
      <Row label="Créée le" value={fmtDate(o.created_at)} />
      <Row label="Dernière MAJ" value={fmtDate(o.updated_at)} />
      <Row label="Source" value={o.source} />
      <Row label="Étape actuelle" value={stageById(o.stage).label} />
      <Row label="Date de signature prévue" value={fmtDate(o.date_signature_prevue)} />
      <Row label="Montant final" value={fmtEur(o.montant_final)} />
    </div>
  );
}

function AudioTab({ calls, audio_files, onReload }) {
  const [uploading, setUploading] = useState(null);
  const [transcriptions, setTranscriptions] = useState({});
  const [retranscribing, setRetranscribing] = useState(null);

  useEffect(() => {
    const ids = (audio_files || []).map((a) => a.transcription_id).filter(Boolean);
    if (!ids.length) return;
    Promise.all(ids.map((id) => api.get(`/transcriptions/${id}`).then((r) => [id, r.data]).catch(() => null)))
      .then((pairs) => {
        const m = Object.fromEntries(pairs.filter(Boolean));
        setTranscriptions(m);
        // Si certains sont encore en processing, re-poll dans 10s
        if (Object.values(m).some((t) => t.status === "processing")) {
          setTimeout(onReload, 10000);
        }
      });
  }, [audio_files, onReload]);

  async function upload(callId, file) {
    setUploading(callId);
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`/calls/${callId}/audio`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Audio uploadé — transcription IA en cours…");
      onReload();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur upload"); }
    finally { setUploading(null); }
  }
  async function remove(id) {
    if (!window.confirm("Supprimer ce fichier audio ?")) return;
    try { await api.delete(`/audio/${id}`); onReload(); toast.success("Supprimé"); }
    catch { toast.error("Erreur"); }
  }
  async function retranscribe(audioId) {
    setRetranscribing(audioId);
    try {
      await api.post(`/audio/${audioId}/transcribe`);
      toast.success("Transcription IA relancée");
      setTimeout(onReload, 5000);
    } catch { toast.error("Erreur"); }
    finally { setRetranscribing(null); }
  }
  const streamUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/commercial/audio/${id}/stream`;

  if (!calls || calls.length === 0) {
    return <div className="rounded-lg border border-brand-line bg-white p-5 text-sm text-brand-subtle">Ajoute d'abord un appel pour pouvoir y attacher un enregistrement audio.</div>;
  }

  return (
    <div className="space-y-3">
      {calls.map((c) => {
        const audio = audio_files.find((a) => a.call_id === c.id);
        const tr = audio ? transcriptions[audio.transcription_id] : null;
        return (
          <div key={c.id} className="rounded-lg border border-brand-line bg-white p-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-sm min-w-0">
                <div className="font-medium text-brand-ink">{fmtDateTime(c.started_at)} · {c.teleprospectrice || "—"}</div>
                <div className="text-xs text-brand-subtle mt-0.5">{c.outcome_status} · {c.note || "—"}</div>
              </div>
              {!audio ? (
                <label className="inline-flex items-center gap-2 text-xs cursor-pointer rounded-md border border-brand-line bg-white px-3 py-1.5 hover:border-brand-ink/25">
                  <Upload className="h-3 w-3" />
                  {uploading === c.id ? "Upload…" : "Importer MP3"}
                  <input type="file" accept=".mp3,.m4a,.wav,.ogg,audio/*" className="hidden" onChange={(e) => e.target.files?.[0] && upload(c.id, e.target.files[0])} />
                </label>
              ) : (
                <div className="flex items-center gap-2">
                  <audio controls src={streamUrl(audio.id)} className="h-8" />
                  <button onClick={() => remove(audio.id)} className="p-1.5 rounded-md border border-brand-line text-brand-subtle hover:text-red-600" aria-label="Supprimer">
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              )}
            </div>
            {audio && (
              <div className="mt-2 text-[11px] text-brand-subtle font-mono">
                {audio.original_filename} · {Math.round((audio.size || 0) / 1024)} Ko · {fmtDate(audio.uploaded_at)}
              </div>
            )}
            {audio && tr && (
              <div className="mt-3 rounded-md border border-brand-line bg-brand-paper p-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="eyebrow">Transcription IA</div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-mono uppercase tracking-wider text-brand-subtle">
                      {tr.status === "processing" ? "⏳ en cours…" : tr.status === "ready" ? "✓ prêt" : tr.status === "failed" ? "✗ échec" : tr.status}
                    </span>
                    <button onClick={() => retranscribe(audio.id)} disabled={retranscribing === audio.id}
                      className="text-[10px] text-brand-subtle hover:text-brand-ink underline underline-offset-2">
                      {retranscribing === audio.id ? "…" : "Relancer"}
                    </button>
                  </div>
                </div>
                {tr.status === "ready" && (
                  <div className="mt-2 space-y-2 text-xs">
                    {tr.summary && <div><span className="font-medium">Résumé :</span> {tr.summary}</div>}
                    {tr.besoin && <div><span className="font-medium">Besoin :</span> {tr.besoin}</div>}
                    {tr.budget && <div><span className="font-medium">Budget :</span> {tr.budget}</div>}
                    {tr.objections && <div><span className="font-medium">Objections :</span> {tr.objections}</div>}
                    {tr.interet != null && <div><span className="font-medium">Intérêt :</span> {tr.interet}/5</div>}
                    {tr.suggestion_relance && <div><span className="font-medium">Relance :</span> {tr.suggestion_relance}</div>}
                    {tr.text && (
                      <details className="mt-1">
                        <summary className="cursor-pointer text-brand-subtle">Voir la transcription complète</summary>
                        <div className="mt-1 whitespace-pre-wrap text-[11px] text-brand-ink/80">{tr.text}</div>
                      </details>
                    )}
                  </div>
                )}
                {tr.status === "failed" && tr.error && <div className="mt-1 text-[11px] text-red-700">{tr.error}</div>}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function PropositionsTab({ o, onUpdate }) {
  return (
    <div className="rounded-lg border border-brand-line bg-white p-5">
      <Field label="Statut proposition">
        <select value={o.stage} onChange={(e) => onUpdate({ stage: e.target.value })} className="h-10 px-3 text-sm rounded-md border border-brand-line bg-white">
          <option value="proposition_a_preparer">À préparer</option>
          <option value="proposition_envoyee">Envoyée</option>
          <option value="negociation">En négociation</option>
        </select>
      </Field>
      <div className="mt-3 text-xs text-brand-subtle">Pour joindre un PDF de proposition, utilise l'onglet Contrat lorsque le document est final.</div>
    </div>
  );
}

function ContratTab({ oppId, contracts, onReload }) {
  const [form, setForm] = useState({ numero: "", montant_ht: "", signed_at: "", duree_mois: "" });
  const [open, setOpen] = useState(false);

  async function create(e) {
    e.preventDefault();
    try {
      await api.post("/contracts", {
        opportunity_id: oppId, numero: form.numero,
        montant_ht: parseFloat(form.montant_ht), signed_at: form.signed_at,
        duree_mois: form.duree_mois ? parseInt(form.duree_mois, 10) : null,
      });
      setOpen(false); onReload(); toast.success("Contrat signé");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  }

  return (
    <div className="space-y-3">
      {!open && !contracts.length && (
        <Button onClick={() => setOpen(true)} data-testid="contract-new"><PlusCircle className="h-4 w-4 mr-2" />Enregistrer le contrat signé</Button>
      )}
      {open && (
        <form onSubmit={create} className="rounded-lg border border-brand-line bg-white p-5 grid sm:grid-cols-2 gap-3">
          <Field label="N° contrat *"><Input required value={form.numero} onChange={(e) => setForm({ ...form, numero: e.target.value })} /></Field>
          <Field label="Montant HT (€) *"><Input required type="number" step="0.01" value={form.montant_ht} onChange={(e) => setForm({ ...form, montant_ht: e.target.value })} /></Field>
          <Field label="Date de signature *"><Input required type="date" value={form.signed_at} onChange={(e) => setForm({ ...form, signed_at: e.target.value })} /></Field>
          <Field label="Durée (mois)"><Input type="number" value={form.duree_mois} onChange={(e) => setForm({ ...form, duree_mois: e.target.value })} /></Field>
          <div className="sm:col-span-2 flex gap-2">
            <Button type="submit">Enregistrer</Button>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
          </div>
        </form>
      )}
      {contracts.map((c) => (
        <div key={c.id} className="rounded-lg border border-brand-line bg-white p-4 text-sm">
          <div className="font-medium">{c.numero} · {fmtEur(c.montant_ht)}</div>
          <div className="text-brand-subtle text-xs mt-1">Signé le {fmtDate(c.signed_at)} · durée {c.duree_mois || "—"} mois</div>
        </div>
      ))}
    </div>
  );
}

function CommissionsTab({ commissions }) {
  if (!commissions.length) return <div className="rounded-lg border border-brand-line bg-white p-5 text-sm text-brand-subtle">Les commissions sont générées automatiquement à la signature du contrat.</div>;
  return (
    <div className="rounded-lg border border-brand-line bg-white divide-y divide-brand-line">
      {commissions.map((c) => (
        <div key={c.id} className="p-4 flex items-center justify-between gap-3 text-sm flex-wrap">
          <div>
            <div className="font-medium text-brand-ink">{c.teleprospectrice} · {fmtEur(c.montant_commission)}</div>
            <div className="text-xs text-brand-subtle mt-0.5">{c.rule_name} · base {fmtEur(c.montant_base)} · {c.periode}</div>
          </div>
          <span className="text-[11px] font-medium px-2 py-1 rounded-md border border-brand-line">{c.status}</span>
        </div>
      ))}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div>
      <div className="eyebrow">{label}</div>
      <div className="mt-1 text-brand-ink">{value || "—"}</div>
    </div>
  );
}
